import { useEffect } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

let homeLenisInstance: Lenis | null = null;

const DEFAULT_SCROLL_DURATION = 0.7;
const DEFAULT_SCROLL_EASING = (t: number) => 1 - Math.pow(1 - t, 3);

export const getHomeLenis = () => {
  return homeLenisInstance;
};

export const scrollHomeTo = (
  top: number,
  options?: {
    immediate?: boolean;
    lock?: boolean;
    onComplete?: () => void;
  },
) => {
  const targetTop = Math.max(0, Math.round(top));

  if (homeLenisInstance) {
    const scrollOptions: NonNullable<Parameters<Lenis['scrollTo']>[1]> = {
      duration: options?.immediate ? 0 : DEFAULT_SCROLL_DURATION,
      easing: DEFAULT_SCROLL_EASING,
      immediate: options?.immediate ?? false,
      lock: options?.lock ?? false,
      force: true,
    };

    if (options?.onComplete) {
      scrollOptions.onComplete = () => {
        options.onComplete?.();
      };
    }

    homeLenisInstance.scrollTo(targetTop, scrollOptions);
    return;
  }

  window.scrollTo({
    top: targetTop,
    behavior: options?.immediate ? 'auto' : 'smooth',
  });

  options?.onComplete?.();
};

const useHomeSmoothScroll = () => {
  useEffect(() => {
    const previousBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return () => {
        document.documentElement.style.scrollBehavior = previousBehavior;
      };
    }

    const lenis = new Lenis({
      autoRaf: false,
      duration: 1.1,
      easing: DEFAULT_SCROLL_EASING,
      smoothWheel: true,
      syncTouch: false,
      gestureOrientation: 'vertical',
      orientation: 'vertical',
      wheelMultiplier: 1,
      touchMultiplier: 1,
      infinite: false,
      autoResize: true,
    });

    homeLenisInstance = lenis;

    const handleLenisScroll = () => {
      ScrollTrigger.update();
    };

    const handleGsapTick = (time: number) => {
      lenis.raf(time * 1000);
    };

    lenis.on('scroll', handleLenisScroll);
    gsap.ticker.add(handleGsapTick, false, true);
    gsap.ticker.lagSmoothing(0);

    return () => {
      lenis.off('scroll', handleLenisScroll);
      gsap.ticker.remove(handleGsapTick);
      lenis.destroy();

      if (homeLenisInstance === lenis) {
        homeLenisInstance = null;
      }

      document.documentElement.style.scrollBehavior = previousBehavior;
    };
  }, []);
};

export default useHomeSmoothScroll;
